import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Plus,
  X,
  User,
  Users,
  Camera,
  Trash2,
  Pencil,
  ChevronLeft,
  Calendar,
  Activity,
  Scale,
  TrendingUp,
  Image as ImageIcon,
  Share2,
  Copy,
  MessageCircle,
  Check,
  FileText,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OBJ_OPTIONS = [
  "Ganho Muscular",
  "Perda de Peso",
  "Perda de Gordura",
  "Tonificação",
  "Resistência",
  "Mobilidade",
  "Pré-Parto",
  "Pós-Parto",
  "Jovens Atletas",
  "Sénior +60 · Força & Resistência",
  "Reabilitação",
  "Saúde Geral",
];

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ClientFormModal({ open, onClose, onSaved, allClients, initial }) {
  const [form, setForm] = useState({
    nome: "",
    foto: "",
    tipo: "individual",
    parceiro_id: "",
    parceiro_novo_nome: "",
    objetivos: [],
    notas: "",
    data_nascimento: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        nome: initial?.nome || "",
        foto: initial?.foto || "",
        tipo: initial?.tipo || "individual",
        parceiro_id: initial?.parceiro_id || "",
        parceiro_novo_nome: "",
        objetivos: initial?.objetivos || [],
        notas: initial?.notas || "",
        data_nascimento: initial?.data_nascimento || "",
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const toggleObj = (o) => {
    setForm((f) => ({
      ...f,
      objetivos: f.objetivos.includes(o)
        ? f.objetivos.filter((x) => x !== o)
        : [...f.objetivos, o],
    }));
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      alert("A foto deve ter menos de 2 MB.");
      return;
    }
    const url = await fileToDataUrl(f);
    setForm((s) => ({ ...s, foto: url }));
  };

  const submit = async () => {
    if (!form.nome.trim()) {
      alert("Indica o nome do cliente.");
      return;
    }
    setSaving(true);
    try {
      const isDuo = form.tipo === "duo";
      const wantsNewPartner =
        isDuo && !form.parceiro_id && (form.parceiro_novo_nome || "").trim();

      if (initial?.id) {
        // Edição — comportamento atual, mas suporta criar+ligar parceiro novo
        let partnerId = form.parceiro_id || null;
        if (wantsNewPartner) {
          const partnerRes = await axios.post(`${API}/clients`, {
            nome: form.parceiro_novo_nome.trim(),
            tipo: "duo",
            parceiro_id: initial.id,
            objetivos: form.objetivos,
          });
          partnerId = partnerRes.data?.id || null;
        }
        await axios.put(`${API}/clients/${initial.id}`, {
          nome: form.nome,
          foto: form.foto,
          tipo: form.tipo,
          parceiro_id: isDuo ? partnerId : null,
          objetivos: form.objetivos,
          notas: form.notas,
          data_nascimento: form.data_nascimento,
        });
      } else {
        // Criação: primeiro o cliente principal
        const primaryRes = await axios.post(`${API}/clients`, {
          nome: form.nome,
          foto: form.foto,
          tipo: form.tipo,
          parceiro_id: isDuo ? form.parceiro_id || null : null,
          objetivos: form.objetivos,
          notas: form.notas,
          data_nascimento: form.data_nascimento,
        });
        const primaryId = primaryRes.data?.id;

        if (isDuo && primaryId) {
          if (wantsNewPartner) {
            // Cria o parceiro e liga-os mutuamente
            const partnerRes = await axios.post(`${API}/clients`, {
              nome: form.parceiro_novo_nome.trim(),
              tipo: "duo",
              parceiro_id: primaryId,
              objetivos: form.objetivos,
            });
            const partnerId = partnerRes.data?.id;
            if (partnerId) {
              await axios.put(`${API}/clients/${primaryId}`, {
                parceiro_id: partnerId,
              });
            }
          } else if (form.parceiro_id) {
            // Liga o parceiro já existente de volta ao principal
            await axios.put(`${API}/clients/${form.parceiro_id}`, {
              parceiro_id: primaryId,
              tipo: "duo",
            });
          }
        }
      }
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      alert("Erro ao guardar cliente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="client-form-modal"
    >
      <div className="card max-w-2xl w-full p-6 md:p-8 my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-3xl uppercase tracking-tighter">
            {initial?.id ? "Editar cliente" : "Novo cliente"}
          </h3>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-yellow-400"
            data-testid="client-form-close"
          >
            <X size={24} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <div className="overline mb-2">Foto</div>
            <label className="block aspect-square border border-neutral-800 hover:border-yellow-400 cursor-pointer relative overflow-hidden bg-neutral-950">
              {form.foto ? (
                <img
                  src={form.foto}
                  alt="cliente"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-neutral-500">
                  <Camera size={32} />
                  <span className="text-xs uppercase tracking-widest mt-2">
                    Carregar
                  </span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={onPickFile}
                className="hidden"
                data-testid="client-photo-input"
              />
            </label>
          </div>

          <div className="md:col-span-2 space-y-4">
            <div>
              <div className="overline mb-2">Nome</div>
              <input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="field"
                placeholder="Ex.: Maria Silva"
                data-testid="client-nome-input"
              />
            </div>
            <div>
              <div className="overline mb-2">Data de nascimento</div>
              <input
                type="date"
                value={form.data_nascimento || ""}
                onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })}
                className="field"
                data-testid="client-nasc-input"
              />
            </div>
            <div>
              <div className="overline mb-2">Tipo de sessão</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="chip"
                  data-active={form.tipo === "individual"}
                  onClick={() => setForm({ ...form, tipo: "individual" })}
                  data-testid="client-tipo-individual"
                >
                  <User size={14} /> Individual
                </button>
                <button
                  type="button"
                  className="chip"
                  data-active={form.tipo === "duo"}
                  onClick={() => setForm({ ...form, tipo: "duo" })}
                  data-testid="client-tipo-duo"
                >
                  <Users size={14} /> Duo
                </button>
              </div>
            </div>
            {form.tipo === "duo" ? (
              <div className="space-y-3">
                <div>
                  <div className="overline mb-2">Parceiro do duo</div>
                  <select
                    value={form.parceiro_id || ""}
                    onChange={(e) =>
                      setForm({ ...form, parceiro_id: e.target.value, parceiro_novo_nome: "" })
                    }
                    className="field"
                    data-testid="client-parceiro-select"
                  >
                    <option value="">— Criar novo abaixo —</option>
                    {(allClients || [])
                      .filter((c) => c.id !== initial?.id)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nome}
                        </option>
                      ))}
                  </select>
                </div>
                {!form.parceiro_id ? (
                  <div>
                    <div className="overline mb-2 text-yellow-400">
                      Nome do parceiro novo
                    </div>
                    <input
                      value={form.parceiro_novo_nome}
                      onChange={(e) =>
                        setForm({ ...form, parceiro_novo_nome: e.target.value })
                      }
                      className="field"
                      placeholder="Ex.: João Silva"
                      data-testid="client-parceiro-novo-input"
                    />
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1.5">
                      Vai ser criado como cliente separado e ligado
                      automaticamente.
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-6">
          <div className="overline mb-2">Objetivos</div>
          <div className="flex flex-wrap gap-2">
            {OBJ_OPTIONS.map((o) => (
              <button
                type="button"
                key={o}
                className="chip"
                data-active={form.objetivos.includes(o)}
                onClick={() => toggleObj(o)}
                data-testid={`client-obj-${o}`}
              >
                {o}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <div className="overline mb-2">Notas</div>
          <textarea
            value={form.notas || ""}
            onChange={(e) => setForm({ ...form, notas: e.target.value })}
            rows={3}
            className="field h-auto py-3 resize-none"
            placeholder="Limitações, lesões, preferências..."
            data-testid="client-notas-input"
          />
        </div>

        <div className="mt-8 flex flex-wrap gap-3 justify-end">
          <button
            onClick={onClose}
            className="btn-ghost"
            data-testid="client-form-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="btn-primary"
            data-testid="client-form-save"
          >
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NumField({ k, label, unit = "", step = "any", form, setForm }) {
  return (
    <div>
      <div className="overline mb-2 truncate">
        {label} {unit ? <span className="text-neutral-600">({unit})</span> : null}
      </div>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="field"
        placeholder="—"
        data-testid={`assess-${k}`}
      />
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-px flex-1 bg-neutral-900" />
        <div className="overline">{title}</div>
        <div className="h-px flex-1 bg-neutral-900" />
      </div>
      {children}
    </div>
  );
}

function AssessmentForm({ clientId, onSaved, onClose, initial }) {
  const today = new Date();
  const [form, setForm] = useState({
    data: today.toISOString().slice(0, 10),
    hora: today.toTimeString().slice(0, 5),
    altura_cm: "",
    peso_kg: "",
    massa_adiposa_pct: "",
    h2o_corporal_pct: "",
    massa_muscular_kg: "",
    densidade_ossea_kg: "",
    idade_metabolica: "",
    gordura_visceral: "",
    nivel_condicao_fisica: "",
    necessidades_caloricas_diarias: "",
    ma_braco_dir: "",
    ma_braco_esq: "",
    ma_perna_dir: "",
    ma_perna_esq: "",
    ma_tronco: "",
    mm_braco_dir: "",
    mm_braco_esq: "",
    mm_perna_dir: "",
    mm_perna_esq: "",
    mm_tronco: "",
    per_abdominal: "",
    per_anca: "",
    per_peito: "",
    per_braco_dir: "",
    per_braco_esq: "",
    per_perna_dir: "",
    per_perna_esq: "",
    notas: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      const f = { ...form };
      Object.keys(form).forEach((k) => {
        if (initial[k] !== undefined && initial[k] !== null) f[k] = initial[k];
      });
      setForm(f);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const NF = (k, label, unit = "", step = "any") => (
    <NumField k={k} label={label} unit={unit} step={step} form={form} setForm={setForm} />
  );

  const imcPreview = (() => {
    const p = parseFloat(form.peso_kg);
    const a = parseFloat(form.altura_cm);
    if (p && a) {
      const m = a / 100;
      return (p / (m * m)).toFixed(2);
    }
    return "—";
  })();

  const submit = async () => {
    setSaving(true);
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (["data", "hora", "notas"].includes(k)) {
        payload[k] = v || null;
      } else if (v !== "" && v !== null) {
        payload[k] = parseFloat(v);
      }
    });
    try {
      const res = await axios.post(`${API}/clients/${clientId}/assessments`, payload);
      onSaved(res?.data || null);
      onClose();
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      let msg = "Erro ao guardar avaliação.";
      if (Array.isArray(detail) && detail.length) {
        msg += "\n" + detail.map((d) => `${d.loc?.join(".") || ""}: ${d.msg}`).join("\n");
      } else if (typeof detail === "string") {
        msg += "\n" + detail;
      }
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const Section = FormSection;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="assessment-form-modal"
    >
      <div className="card max-w-5xl w-full p-6 md:p-8 my-8">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-3xl uppercase tracking-tighter">
            Nova avaliação física
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-yellow-400">
            <X size={24} />
          </button>
        </div>
        <p className="text-sm text-neutral-500 mb-6">
          Preenche os campos disponíveis. Os campos em branco são ignorados.
        </p>

        <Section title="Data e Hora">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="overline mb-2">Data</div>
              <input
                type="date"
                value={form.data}
                onChange={(e) => setForm({ ...form, data: e.target.value })}
                className="field"
                data-testid="assess-data"
              />
            </div>
            <div>
              <div className="overline mb-2">Hora</div>
              <input
                type="time"
                value={form.hora}
                onChange={(e) => setForm({ ...form, hora: e.target.value })}
                className="field"
                data-testid="assess-hora"
              />
            </div>
          </div>
        </Section>

        <Section title="Composição Corporal">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {NF("altura_cm", "Altura", "cm")}
            {NF("peso_kg", "Peso", "kg")}
            <div>
              <div className="overline mb-2">
                IMC <span className="text-neutral-600">(auto)</span>
              </div>
              <div className="field flex items-center text-yellow-400 font-display">
                {imcPreview}
              </div>
            </div>
            {NF("massa_adiposa_pct", "Massa adiposa", "%")}
            {NF("h2o_corporal_pct", "H₂O corporal", "%")}
            {NF("massa_muscular_kg", "Massa muscular", "kg")}
            {NF("densidade_ossea_kg", "Densidade óssea", "kg")}
            {NF("idade_metabolica", "Idade metabólica", "anos")}
            {NF("gordura_visceral", "Gordura visceral")}
            {NF("nivel_condicao_fisica", "Nível condição física")}
            {NF("necessidades_caloricas_diarias", "Necessidades calóricas", "kcal/dia")}
          </div>
        </Section>

        <Section title="Massa Adiposa por Segmento (%)">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {NF("ma_braco_dir", "Braço direito", "%")}
            {NF("ma_braco_esq", "Braço esquerdo", "%")}
            {NF("ma_tronco", "Tronco", "%")}
            {NF("ma_perna_dir", "Perna direita", "%")}
            {NF("ma_perna_esq", "Perna esquerda", "%")}
          </div>
        </Section>

        <Section title="Massa Muscular por Segmento (kg)">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {NF("mm_braco_dir", "Braço direito", "kg")}
            {NF("mm_braco_esq", "Braço esquerdo", "kg")}
            {NF("mm_tronco", "Tronco", "kg")}
            {NF("mm_perna_dir", "Perna direita", "kg")}
            {NF("mm_perna_esq", "Perna esquerda", "kg")}
          </div>
        </Section>

        <Section title="Perímetros (cm)">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {NF("per_abdominal", "Abdominal", "cm")}
            {NF("per_anca", "Anca", "cm")}
            {NF("per_peito", "Peito", "cm")}
            <div />
            {NF("per_braco_dir", "Braço direito", "cm")}
            {NF("per_braco_esq", "Braço esquerdo", "cm")}
            {NF("per_perna_dir", "Perna direita", "cm")}
            {NF("per_perna_esq", "Perna esquerda", "cm")}
          </div>
        </Section>

        <Section title="Notas">
          <textarea
            rows={3}
            value={form.notas}
            onChange={(e) => setForm({ ...form, notas: e.target.value })}
            className="field h-auto py-3 resize-none"
            placeholder="Observações da avaliação..."
          />
        </Section>

        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="btn-primary"
            data-testid="assess-save"
          >
            {saving ? "A guardar..." : "Guardar avaliação"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionForm({ clientId, defaultTipo, onSaved, onClose }) {
  const [form, setForm] = useState({
    data: new Date().toISOString().slice(0, 10),
    tipo: defaultTipo || "individual",
    duracao_min: 60,
    titulo: "",
    notas: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/sessions`, {
        ...form,
        duracao_min: parseInt(form.duracao_min, 10) || 60,
      });
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      alert("Erro ao registar sessão.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="session-form-modal"
    >
      <div className="card max-w-xl w-full p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl uppercase tracking-tighter">
            Registar sessão
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-yellow-400">
            <X size={22} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="overline mb-2">Data</div>
            <input
              type="date"
              value={form.data}
              onChange={(e) => setForm({ ...form, data: e.target.value })}
              className="field"
              data-testid="session-data"
            />
          </div>
          <div>
            <div className="overline mb-2">Duração (min)</div>
            <input
              type="number"
              value={form.duracao_min}
              onChange={(e) => setForm({ ...form, duracao_min: e.target.value })}
              className="field"
              data-testid="session-duracao"
            />
          </div>
        </div>
        <div className="mt-4">
          <div className="overline mb-2">Tipo</div>
          <div className="flex gap-2">
            <button
              type="button"
              className="chip"
              data-active={form.tipo === "individual"}
              onClick={() => setForm({ ...form, tipo: "individual" })}
              data-testid="session-tipo-individual"
            >
              <User size={14} /> Individual
            </button>
            <button
              type="button"
              className="chip"
              data-active={form.tipo === "duo"}
              onClick={() => setForm({ ...form, tipo: "duo" })}
              data-testid="session-tipo-duo"
            >
              <Users size={14} /> Duo
            </button>
          </div>
        </div>
        <div className="mt-4">
          <div className="overline mb-2">Título / Foco</div>
          <input
            value={form.titulo}
            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            className="field"
            placeholder="Ex.: Treino de pernas"
            data-testid="session-titulo"
          />
        </div>
        <div className="mt-4">
          <div className="overline mb-2">Notas</div>
          <textarea
            rows={3}
            value={form.notas}
            onChange={(e) => setForm({ ...form, notas: e.target.value })}
            className="field h-auto py-3 resize-none"
          />
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="btn-primary"
            data-testid="session-save"
          >
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function calcIdade(nascimento) {
  if (!nascimento) return null;
  const n = new Date(nascimento);
  if (isNaN(n.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - n.getFullYear();
  const m = today.getMonth() - n.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < n.getDate())) age--;
  return age;
}

function AssessmentDetail({ a, onClose }) {
  if (!a) return null;
  const Row = ({ label, value, unit }) => (
    <div className="flex justify-between py-2 border-b border-neutral-900 text-sm">
      <span className="text-neutral-400">{label}</span>
      <span className="text-white font-medium">
        {value ?? "—"} {value != null && unit ? <span className="text-neutral-500">{unit}</span> : null}
      </span>
    </div>
  );
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="assess-detail-modal"
    >
      <div className="card max-w-3xl w-full p-6 md:p-8 my-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline">Avaliação Física</div>
            <h3 className="font-display text-2xl uppercase tracking-tighter">
              {a.data} {a.hora ? `· ${a.hora}` : ""}
            </h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-yellow-400">
            <X size={24} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <div>
            <div className="overline mb-2 text-yellow-400">Composição Corporal</div>
            <Row label="Altura" value={a.altura_cm} unit="cm" />
            <Row label="Peso" value={a.peso_kg} unit="kg" />
            <Row label="IMC" value={a.imc} />
            <Row label="Massa adiposa" value={a.massa_adiposa_pct} unit="%" />
            <Row label="H₂O corporal" value={a.h2o_corporal_pct} unit="%" />
            <Row label="Massa muscular" value={a.massa_muscular_kg} unit="kg" />
            <Row label="Densidade óssea" value={a.densidade_ossea_kg} unit="kg" />
            <Row label="Idade metabólica" value={a.idade_metabolica} unit="anos" />
            <Row label="Gordura visceral" value={a.gordura_visceral} />
            <Row label="Nível condição física" value={a.nivel_condicao_fisica} />
            <Row label="Necessidades calóricas" value={a.necessidades_caloricas_diarias} unit="kcal/dia" />
          </div>
          <div>
            <div className="overline mb-2 text-yellow-400">Massa Adiposa Segmentos (%)</div>
            <Row label="Braço dir." value={a.ma_braco_dir} unit="%" />
            <Row label="Braço esq." value={a.ma_braco_esq} unit="%" />
            <Row label="Tronco" value={a.ma_tronco} unit="%" />
            <Row label="Perna dir." value={a.ma_perna_dir} unit="%" />
            <Row label="Perna esq." value={a.ma_perna_esq} unit="%" />

            <div className="overline mb-2 mt-5 text-yellow-400">Massa Muscular Segmentos (kg)</div>
            <Row label="Braço dir." value={a.mm_braco_dir} unit="kg" />
            <Row label="Braço esq." value={a.mm_braco_esq} unit="kg" />
            <Row label="Tronco" value={a.mm_tronco} unit="kg" />
            <Row label="Perna dir." value={a.mm_perna_dir} unit="kg" />
            <Row label="Perna esq." value={a.mm_perna_esq} unit="kg" />

            <div className="overline mb-2 mt-5 text-yellow-400">Perímetros (cm)</div>
            <Row label="Abdominal" value={a.per_abdominal} unit="cm" />
            <Row label="Anca" value={a.per_anca} unit="cm" />
            <Row label="Peito" value={a.per_peito} unit="cm" />
            <Row label="Braço dir." value={a.per_braco_dir} unit="cm" />
            <Row label="Braço esq." value={a.per_braco_esq} unit="cm" />
            <Row label="Perna dir." value={a.per_perna_dir} unit="cm" />
            <Row label="Perna esq." value={a.per_perna_esq} unit="cm" />
          </div>
        </div>
        {a.notas ? (
          <div className="mt-6 border-t border-neutral-900 pt-4">
            <div className="overline mb-2">Notas</div>
            <p className="text-sm text-neutral-300">{a.notas}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AssessmentComparisonChart({ assessments }) {
  // Pegar as últimas 3 (já ordenadas asc)
  const last3 = assessments.slice(-3);
  // Construir dados para gráfico agrupado
  const metrics = [
    { key: "peso_kg", label: "Peso (kg)" },
    { key: "imc", label: "IMC" },
    { key: "massa_adiposa_pct", label: "% Adiposa" },
    { key: "h2o_corporal_pct", label: "% H₂O" },
    { key: "massa_muscular_kg", label: "Músculo (kg)" },
    { key: "gordura_visceral", label: "Visceral" },
    { key: "necessidades_caloricas_diarias", label: "Kcal/dia" },
    { key: "per_abdominal", label: "Abdominal (cm)" },
    { key: "per_anca", label: "Anca (cm)" },
  ];
  const data = metrics.map((m) => {
    const row = { metrica: m.label };
    last3.forEach((a) => {
      row[a.data] = a[m.key] ?? null;
    });
    return row;
  });
  const colors = ["#737373", "#facc15", "#fafafa"]; // mais escuro → mais recente

  // Filtrar métricas sem dados em nenhuma das 3
  const filtered = data.filter((row) =>
    last3.some((a) => row[a.data] != null)
  );

  if (filtered.length === 0) return null;

  return (
    <div className="card p-5 md:p-6 mb-6 bg-black/70 backdrop-blur-sm" data-testid="assessment-comparison-chart">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline mb-1">Comparação · últimas {last3.length} avaliações</div>
          <h4 className="font-display text-xl uppercase tracking-tight">
            Evolução por métrica
          </h4>
        </div>
        <div className="flex gap-2">
          {last3.map((a, i) => (
            <div
              key={a.id}
              className="flex items-center gap-2 text-xs uppercase tracking-widest"
            >
              <span
                className="inline-block w-3 h-3"
                style={{ background: colors[i] }}
              />
              <span className="text-neutral-400">{a.data}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ width: "100%", height: 320 }}>
        <ResponsiveContainer>
          <BarChart data={filtered} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid stroke="#1f1f1f" strokeDasharray="2 4" />
            <XAxis
              dataKey="metrica"
              stroke="#666"
              tick={{ fontSize: 10 }}
              interval={0}
              angle={-15}
              textAnchor="end"
              height={60}
            />
            <YAxis stroke="#666" tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{
                background: "#0a0a0a",
                border: "1px solid #262626",
                color: "#fafafa",
              }}
            />
            <Legend wrapperStyle={{ color: "#a3a3a3", fontSize: 11 }} />
            {last3.map((a, i) => (
              <Bar
                key={a.id}
                dataKey={a.data}
                name={a.data}
                fill={colors[i]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
function DuoAssessmentPanel({ title, items, onView, onDelete }) {
  return (
    <div className="border border-neutral-800 bg-black/70 backdrop-blur-sm">
      <div className="px-4 py-3 border-b border-neutral-800 bg-neutral-950/80 flex items-center justify-between">
        <div className="font-display text-lg uppercase tracking-tight truncate">
          {title}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
          {items.length} {items.length === 1 ? "aval." : "avals."}
        </div>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-neutral-500 text-sm">
          Sem avaliações registadas.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-neutral-500">
              <tr>
                {["Data", "Peso", "IMC", "% Adip.", "Musc.", ""].map((h) => (
                  <th
                    key={h}
                    className="text-left px-3 py-2 text-[10px] uppercase tracking-widest"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items
                .slice()
                .reverse()
                .map((a) => (
                  <tr
                    key={a.id}
                    className="border-t border-neutral-900 hover:bg-neutral-950/50 cursor-pointer"
                    onClick={() => onView(a)}
                    data-testid={`duo-assess-row-${a.id}`}
                  >
                    <td className="px-3 py-2 font-medium">{a.data}</td>
                    <td className="px-3 py-2">{a.peso_kg ?? "—"}</td>
                    <td className="px-3 py-2 text-yellow-400">{a.imc ?? "—"}</td>
                    <td className="px-3 py-2">{a.massa_adiposa_pct ?? "—"}</td>
                    <td className="px-3 py-2">{a.massa_muscular_kg ?? "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(a.id);
                        }}
                        className="text-neutral-500 hover:text-yellow-400"
                        data-testid={`duo-assess-del-${a.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
function ShareAssessmentModal({ open, onClose, targetClient, assessment, prevAssessment }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  if (!open || !targetClient || !assessment) return null;

  const fmt = (v, u = "") => (v != null && v !== "" ? `${v}${u ? " " + u : ""}` : "—");
  const delta = (curr, prev, u = "") => {
    if (curr == null || prev == null) return null;
    const d = parseFloat(curr) - parseFloat(prev);
    if (isNaN(d)) return null;
    const sign = d > 0 ? "+" : "";
    const arrow = d > 0 ? "▲" : d < 0 ? "▼" : "▬";
    return `${arrow} ${sign}${d.toFixed(1)}${u}`;
  };

  const first = (targetClient.nome || "").split(" ")[0];
  const linhasAval = [
    assessment.peso_kg != null ? `⚖️ Peso: ${fmt(assessment.peso_kg, "kg")}` : null,
    assessment.imc != null ? `📈 IMC: ${fmt(assessment.imc)}` : null,
    assessment.massa_adiposa_pct != null ? `🔥 Massa adiposa: ${fmt(assessment.massa_adiposa_pct, "%")}` : null,
    assessment.massa_muscular_kg != null ? `💪 Massa muscular: ${fmt(assessment.massa_muscular_kg, "kg")}` : null,
    assessment.gordura_visceral != null ? `🩺 Gordura visceral: ${fmt(assessment.gordura_visceral)}` : null,
    assessment.per_abdominal != null ? `📏 Perímetro abdominal: ${fmt(assessment.per_abdominal, "cm")}` : null,
    assessment.idade_metabolica != null ? `🧬 Idade metabólica: ${fmt(assessment.idade_metabolica, "anos")}` : null,
  ].filter(Boolean);

  let evolucaoTxt = "";
  if (prevAssessment) {
    const items = [
      ["Peso", delta(assessment.peso_kg, prevAssessment.peso_kg, " kg")],
      ["IMC", delta(assessment.imc, prevAssessment.imc)],
      ["Massa adiposa", delta(assessment.massa_adiposa_pct, prevAssessment.massa_adiposa_pct, "%")],
      ["Massa muscular", delta(assessment.massa_muscular_kg, prevAssessment.massa_muscular_kg, " kg")],
      ["Perímetro abd.", delta(assessment.per_abdominal, prevAssessment.per_abdominal, " cm")],
    ].filter(([_, v]) => v);
    if (items.length) {
      evolucaoTxt =
        `\n\n📊 EVOLUÇÃO desde ${prevAssessment.data}:\n` +
        items.map(([l, v]) => `▸ ${l}: ${v}`).join("\n");
    }
  }

  const text =
    `Olá ${first}! 💪\n\n` +
    `Segue a tua nova avaliação de ${assessment.data}${assessment.hora ? " às " + assessment.hora : ""}:\n\n` +
    linhasAval.join("\n") +
    evolucaoTxt +
    (assessment.notas ? `\n\n📝 Notas: ${assessment.notas}` : "") +
    `\n\nContinua com este foco. Vamos por mais!\n` +
    `Cumprimentos do PT e amigo,\nPedro Pando · O-meu-PT.pt`;

  const encoded = encodeURIComponent(text);
  const phone = (targetClient.telefone || "").replace(/[^\d+]/g, "");
  const waUrl = phone ? `https://wa.me/${phone.replace(/^\+/, "")}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
  const subject = encodeURIComponent(`A tua avaliação · ${assessment.data} · O-MEU-PT.PT`);
  const emailTo = targetClient.email || "";
  const emailUrl = `mailto:${emailTo}?subject=${subject}&body=${encoded}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/clients/${targetClient.id}/assessments/${assessment.id}/pdf`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      const safe = (targetClient.nome || "cliente").replace(/\s+/g, "_");
      link.href = url;
      link.download = `Avaliacao_${safe}_${assessment.data}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert("Não foi possível gerar o PDF: " + (e?.response?.status || e?.message || "erro"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="share-assessment-modal"
      onClick={onClose}
    >
      <div
        className="bg-neutral-950 border border-yellow-400/60 max-w-lg w-full p-6 md:p-8 my-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-400 hover:text-yellow-400"
          data-testid="share-modal-close"
          aria-label="Fechar"
        >
          <X size={22} />
        </button>
        <div className="flex items-center gap-3 text-yellow-400 text-[10px] uppercase tracking-[0.32em] font-black mb-3">
          <span className="w-8 h-[2px] bg-yellow-400" />
          <span>Avaliação Guardada</span>
        </div>
        <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tighter leading-tight mb-2">
          Partilhar com <span className="text-yellow-400">{first}</span>
        </h3>
        <p className="text-neutral-400 text-sm mb-4">
          Envia o resumo desta avaliação {prevAssessment ? "e a evolução " : ""}
          diretamente por WhatsApp ou email.
        </p>

        <div className="bg-black/70 border border-neutral-800 p-4 max-h-56 overflow-y-auto text-xs whitespace-pre-wrap text-neutral-300 mb-4 font-mono">
          {text}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA58] text-black font-black uppercase tracking-widest text-xs px-5 py-3 transition-colors"
            data-testid="share-whatsapp-btn"
          >
            WhatsApp
          </a>
          <a
            href={emailUrl}
            className={`inline-flex items-center justify-center gap-2 font-black uppercase tracking-widest text-xs px-5 py-3 transition-colors ${
              emailTo
                ? "bg-yellow-400 hover:bg-yellow-300 text-black"
                : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
            }`}
            data-testid="share-email-btn"
            onClick={(e) => {
              if (!emailTo) e.preventDefault();
            }}
          >
            {emailTo ? "Email" : "Sem email"}
          </a>
        </div>
        <button
          onClick={downloadPdf}
          disabled={downloading}
          className="w-full inline-flex items-center justify-center gap-2 border border-yellow-400/60 text-yellow-400 hover:bg-yellow-400 hover:text-black font-black uppercase tracking-widest text-xs px-5 py-3 mb-2 transition-colors disabled:opacity-60"
          data-testid="share-pdf-btn"
        >
          {downloading ? "A gerar PDF…" : "Descarregar PDF · Anexo"}
        </button>
        <button
          onClick={copy}
          className="w-full text-yellow-400 hover:text-yellow-300 text-[10px] uppercase tracking-[0.32em] font-black py-2"
          data-testid="share-copy-btn"
        >
          {copied ? "✓ Texto copiado" : "Copiar texto"}
        </button>
        <div className="mt-2 text-[10px] uppercase tracking-widest text-neutral-500 text-center">
          {!phone && "Sem telefone: o WhatsApp abre em modo escolher contacto"}
        </div>
      </div>
    </div>
  );
}





function PhotoGalleryForm({ clientId, onSaved, onClose }) {
  const [form, setForm] = useState({
    foto: "",
    data: new Date().toISOString().slice(0, 10),
    notas: "",
  });
  const [saving, setSaving] = useState(false);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      alert("A foto deve ter menos de 3 MB.");
      return;
    }
    const url = await fileToDataUrl(f);
    setForm((s) => ({ ...s, foto: url }));
  };

  const submit = async () => {
    if (!form.foto) {
      alert("Carrega uma foto primeiro.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/photos`, form);
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      alert("Erro ao guardar foto.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="photo-form-modal"
    >
      <div className="card max-w-xl w-full p-6 md:p-8 my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl uppercase tracking-tighter">
            Nova foto · galeria
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-yellow-400">
            <X size={22} />
          </button>
        </div>

        <label className="block border border-neutral-800 hover:border-yellow-400 cursor-pointer relative overflow-hidden bg-neutral-950 aspect-video">
          {form.foto ? (
            <img src={form.foto} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-neutral-500">
              <Camera size={40} />
              <span className="text-xs uppercase tracking-widest mt-2">
                Carregar foto (até 3 MB)
              </span>
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={onPickFile}
            className="hidden"
            data-testid="photo-file-input"
          />
        </label>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <div className="overline mb-2">Data</div>
            <input
              type="date"
              value={form.data}
              onChange={(e) => setForm({ ...form, data: e.target.value })}
              className="field"
              data-testid="photo-data"
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="overline mb-2">Notas (opcional)</div>
          <input
            value={form.notas}
            onChange={(e) => setForm({ ...form, notas: e.target.value })}
            className="field"
            placeholder="Ex.: foto pós-treino, evolução semana 4..."
            data-testid="photo-notas"
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="btn-primary"
            data-testid="photo-save"
          >
            {saving ? "A guardar..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Avatar({ src, name, size = 64 }) {
  const initials = (name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="bg-neutral-900 border border-neutral-800 flex items-center justify-center font-display text-yellow-400"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initials || "?"}
    </div>
  );
}

function ClientCard({ c, onOpen }) {
  return (
    <button
      onClick={() => onOpen(c.id)}
      className="card p-5 text-left hover:border-yellow-400/60 transition-colors group"
      data-testid={`client-card-${c.id}`}
    >
      <div className="flex items-start gap-4">
        <Avatar src={c.foto} name={c.nome} size={72} />
        <div className="flex-1 min-w-0">
          <div className="font-display uppercase text-xl tracking-tight truncate group-hover:text-yellow-400">
            {c.nome}
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            <span className="chip">
              {c.tipo === "duo" ? (
                <>
                  <Users size={12} /> Duo
                </>
              ) : (
                <>
                  <User size={12} /> Individual
                </>
              )}
            </span>
            {(c.objetivos || []).slice(0, 2).map((o) => (
              <span key={o} className="chip">
                {o}
              </span>
            ))}
            {c.objetivos && c.objetivos.length > 2 ? (
              <span className="chip">+{c.objetivos.length - 2}</span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-5 border-t border-neutral-900 pt-4">
        <div>
          <div className="font-display text-2xl text-yellow-400">
            {c.sessoes_este_mes ?? 0}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            Mês atual
          </div>
        </div>
        <div>
          <div className="font-display text-2xl">{c.sessoes_total ?? 0}</div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            Total
          </div>
        </div>
        <div>
          <div className="font-display text-2xl">
            {c.ultima_avaliacao?.peso_kg ? `${c.ultima_avaliacao.peso_kg}` : "—"}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            Peso (kg)
          </div>
        </div>
      </div>
    </button>
  );
}

function ClientDetail({ clientId, onBack, onChanged, allClients }) {
  const [client, setClient] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [partnerAssessments, setPartnerAssessments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [tab, setTab] = useState("avaliacoes");
  const [showAssess, setShowAssess] = useState(false);
  const [assessTargetId, setAssessTargetId] = useState(null);
  const [shareData, setShareData] = useState(null); // {client, assessment, prev}
  const [showSession, setShowSession] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  const [viewAssess, setViewAssess] = useState(null);
  const [sessionFilter, setSessionFilter] = useState("todos");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const reload = async () => {
    const [c, a, s, ph] = await Promise.all([
      axios.get(`${API}/clients/${clientId}`),
      axios.get(`${API}/clients/${clientId}/assessments`),
      axios.get(`${API}/clients/${clientId}/sessions`),
      axios.get(`${API}/clients/${clientId}/photos`),
    ]);
    setClient(c.data);
    setAssessments(a.data);
    setSessions(s.data);
    setPhotos(ph.data);
    // Se é duo com parceiro, carrega também as avaliações do parceiro
    const partnerId = c.data?.parceiro_id || c.data?.parceiro?.id;
    if (c.data?.tipo === "duo" && partnerId) {
      try {
        const pa = await axios.get(`${API}/clients/${partnerId}/assessments`);
        setPartnerAssessments(pa.data);
      } catch {
        setPartnerAssessments([]);
      }
    } else {
      setPartnerAssessments([]);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const shareText = useMemo(() => {
    if (!client) return "";
    const today = new Date();
    const curMonth = today.getMonth();
    const curYear = today.getFullYear();

    const inMonth = [...sessions]
      .filter((s) => {
        if (!s.data) return false;
        try {
          const d = new Date(s.data);
          return d <= today && d.getMonth() === curMonth && d.getFullYear() === curYear;
        } catch {
          return false;
        }
      })
      .sort((a, b) => (a.data > b.data ? 1 : -1));

    if (inMonth.length === 0) {
      return `Olá ${client.nome}!\n\nAinda não treinámos este mês.\n\nCumprimentos do PT e amigo,\nPedro Pando · O-meu-PT.pt`;
    }

    const linhas = inMonth
      .map((s) => {
        let dateStr = s.data;
        try {
          const d = new Date(s.data);
          dateStr = d.toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" });
        } catch {}
        return `• ${dateStr}`;
      })
      .join("\n");

    const intro = `Olá ${client.nome}! 💪\n\nNeste mês treinámos ${inMonth.length} ${inMonth.length === 1 ? "vez" : "vezes"} nas seguintes datas:\n\n${linhas}`;
    const outro = `\n\nObrigado pelo teu compromisso, foco e determinação.\nVamos por mais!\nSeguimos juntos rumo aos teus objetivos!\n\nCumprimentos do PT e amigo,\nPedro Pando · O-meu-PT.pt`;

    return intro + outro;
  }, [client, sessions]);

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  if (!client) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <button onClick={onBack} className="btn-ghost text-xs">
          <ChevronLeft size={14} /> Voltar
        </button>
        <p className="mt-6 text-neutral-500">A carregar…</p>
      </div>
    );
  }

  const evolutionData = assessments
    .filter((a) => a.peso_kg || a.massa_adiposa_pct)
    .map((a) => ({
      data: a.data,
      peso: a.peso_kg ?? null,
      massa_adiposa: a.massa_adiposa_pct ?? null,
    }));

  const filteredSessions =
    sessionFilter === "todos"
      ? sessions
      : sessions.filter((s) => s.tipo === sessionFilter);

  const removeAssessment = async (aid, targetId = null) => {
    if (!window.confirm("Apagar esta avaliação?")) return;
    const cid = targetId || clientId;
    await axios.delete(`${API}/clients/${cid}/assessments/${aid}`);
    reload();
  };

  const removeSession = async (sid) => {
    if (!window.confirm("Apagar esta sessão?")) return;
    await axios.delete(`${API}/clients/${clientId}/sessions/${sid}`);
    reload();
    onChanged && onChanged();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (e) {
      console.error("copy fail", e);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Treinos · ${client?.nome}`,
          text: shareText,
        });
      } catch {}
    } else {
      handleCopy();
    }
  };

  const removePhoto = async (pid) => {
    if (!window.confirm("Apagar esta foto?")) return;
    await axios.delete(`${API}/clients/${clientId}/photos/${pid}`);
    reload();
  };

  const removeClient = async () => {
    if (!window.confirm(`Apagar cliente ${client.nome}? Esta ação é irreversível.`))
      return;
    await axios.delete(`${API}/clients/${clientId}`);
    onChanged && onChanged();
    onBack();
  };

  return (
    <div className="px-6 md:px-12 lg:px-24 py-10 md:py-14">
      <button
        onClick={onBack}
        className="btn-ghost text-xs inline-flex items-center gap-1"
        data-testid="client-back"
      >
        <ChevronLeft size={14} /> Voltar
      </button>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
        <div className="md:col-span-3 card p-5">
          <Avatar src={client.foto} name={client.nome} size={220} />
        </div>
        <div className="md:col-span-9">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="overline mb-2">
                {client.tipo === "duo" ? "Cliente · Duo" : "Cliente · Individual"}
                {client.parceiro?.nome ? ` · com ${client.parceiro.nome}` : ""}
                {calcIdade(client.data_nascimento) != null
                  ? ` · ${calcIdade(client.data_nascimento)} anos`
                  : ""}
              </div>
              <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-none">
                {client.nome}
              </h1>
            </div>
            <button
              onClick={removeClient}
              className="btn-ghost text-xs inline-flex items-center gap-2"
              data-testid="client-delete-btn"
            >
              <Trash2 size={12} /> Apagar
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setShowEdit(true)}
              className="btn-ghost text-xs inline-flex items-center gap-2"
              data-testid="client-edit-btn"
            >
              <Pencil size={12} /> Editar cliente
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {(client.objetivos || []).map((o) => (
              <span key={o} className="chip" data-active="true">
                {o}
              </span>
            ))}
          </div>
          {client.notas ? (
            <p className="mt-4 text-sm text-neutral-400 leading-relaxed">
              {client.notas}
            </p>
          ) : null}

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border border-neutral-800 p-4">
              <div className="font-display text-3xl text-yellow-400">
                {client.sessoes_este_mes ?? 0}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-neutral-500">
                Treinos este mês
              </div>
            </div>
            <div className="border border-neutral-800 p-4">
              <div className="font-display text-3xl">{client.sessoes_total ?? 0}</div>
              <div className="text-[11px] uppercase tracking-widest text-neutral-500">
                Treinos totais
              </div>
            </div>
            <div className="border border-neutral-800 p-4">
              <div className="font-display text-3xl">{assessments.length}</div>
              <div className="text-[11px] uppercase tracking-widest text-neutral-500">
                Avaliações
              </div>
            </div>
            <div className="border border-neutral-800 p-4">
              <div className="font-display text-3xl">
                {assessments[assessments.length - 1]?.peso_kg ?? "—"}
                <span className="text-sm text-neutral-500 ml-1">kg</span>
              </div>
              <div className="text-[11px] uppercase tracking-widest text-neutral-500">
                Último peso
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 flex gap-2 border-b border-neutral-900">
        {[
          { id: "avaliacoes", label: "Avaliações" },
          { id: "sessoes", label: "Sessões" },
          { id: "evolucao", label: "Evolução" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-xs uppercase tracking-widest font-bold border-b-2 transition-colors ${
              tab === t.id
                ? "text-yellow-400 border-yellow-400"
                : "text-neutral-500 border-transparent hover:text-neutral-300"
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "avaliacoes" ? (
        <div className="mt-6 relative">
          <div
            className="absolute inset-0 bg-cover bg-center pointer-events-none"
            style={{
              backgroundImage: `url(https://customer-assets.emergentagent.com/job_personal-trainer-109/artifacts/pvjt0dp6_IMG_20260504_142804.jpg)`,
              filter: "brightness(0.40)",
            }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 bg-black/55 pointer-events-none"
            aria-hidden="true"
          />
          <div className="relative p-4 md:p-6">
          {client.tipo === "duo" && client.parceiro?.id ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="overline">Vista Duo · duas colunas</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.get(
                          `${API}/clients/${clientId}/duo-pdf`,
                          { responseType: "blob" }
                        );
                        const url = URL.createObjectURL(
                          new Blob([res.data], { type: "application/pdf" })
                        );
                        const a = document.createElement("a");
                        a.href = url;
                        const safeA = (client.nome || "a").replace(/\s+/g, "_");
                        const safeB = (client.parceiro.nome || "b").replace(/\s+/g, "_");
                        a.download = `AvaliacaoDuo_${safeA}_vs_${safeB}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                      } catch (e) {
                        alert("Não foi possível gerar o PDF Duo.");
                      }
                    }}
                    className="inline-flex items-center gap-2 border border-yellow-400/60 text-yellow-400 hover:bg-yellow-400 hover:text-black font-black uppercase tracking-widest text-[10px] px-3 py-2 transition-colors"
                    data-testid="duo-pdf-btn"
                  >
                    <FileText size={14} /> PDF Duo
                  </button>
                  <button
                    onClick={() => { setAssessTargetId(clientId); setShowAssess(true); }}
                    className="btn-primary inline-flex items-center gap-2 text-xs"
                    data-testid="add-assessment-btn-self"
                  >
                    <Plus size={14} /> Nova · {client.nome.split(" ")[0]}
                  </button>
                  <button
                    onClick={() => { setAssessTargetId(client.parceiro.id); setShowAssess(true); }}
                    className="btn-primary inline-flex items-center gap-2 text-xs"
                    data-testid="add-assessment-btn-partner"
                  >
                    <Plus size={14} /> Nova · {client.parceiro.nome.split(" ")[0]}
                  </button>
                </div>
              </div>

              {assessments.length >= 2 ? (
                <AssessmentComparisonChart assessments={assessments} />
              ) : null}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                <DuoAssessmentPanel
                  title={client.nome}
                  items={assessments}
                  onView={setViewAssess}
                  onDelete={(aid) => removeAssessment(aid, clientId)}
                />
                <DuoAssessmentPanel
                  title={client.parceiro.nome}
                  items={partnerAssessments}
                  onView={setViewAssess}
                  onDelete={(aid) => removeAssessment(aid, client.parceiro.id)}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => { setAssessTargetId(clientId); setShowAssess(true); }}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="add-assessment-btn"
                >
                  <Plus size={16} /> Nova avaliação
                </button>
              </div>

              {assessments.length >= 2 ? (
                <AssessmentComparisonChart assessments={assessments} />
              ) : null}

              {assessments.length === 0 ? (
                <div className="card p-8 text-center text-neutral-500">
                  Sem avaliações registadas.
                </div>
              ) : (
                <div className="overflow-x-auto border border-neutral-800 bg-black/70 backdrop-blur-sm">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-950/80 text-neutral-500">
                      <tr>
                        {["Data", "Hora", "Peso", "IMC", "% Adiposa", "Musc. (kg)", "Visceral", "Abdominal", ""].map((h) => (
                          <th key={h} className="text-left px-3 py-3 text-[10px] uppercase tracking-widest">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {assessments.slice().reverse().map((a) => (
                        <tr
                          key={a.id}
                          className="border-t border-neutral-900 hover:bg-neutral-950/50 cursor-pointer"
                          onClick={() => setViewAssess(a)}
                          data-testid={`assess-row-${a.id}`}
                        >
                          <td className="px-3 py-3 font-medium">{a.data}</td>
                          <td className="px-3 py-3 text-neutral-400">{a.hora || "—"}</td>
                          <td className="px-3 py-3">{a.peso_kg ?? "—"} kg</td>
                          <td className="px-3 py-3 text-yellow-400">{a.imc ?? "—"}</td>
                          <td className="px-3 py-3">{a.massa_adiposa_pct ?? "—"}</td>
                          <td className="px-3 py-3">{a.massa_muscular_kg ?? "—"}</td>
                          <td className="px-3 py-3">{a.gordura_visceral ?? "—"}</td>
                          <td className="px-3 py-3">{a.per_abdominal ?? "—"}</td>
                          <td className="px-3 py-3 text-right">
                            <button
                              onClick={(e) => { e.stopPropagation(); removeAssessment(a.id); }}
                              className="text-neutral-500 hover:text-yellow-400"
                              data-testid={`assess-del-${a.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      ) : null}

      {tab === "sessoes" ? (
        <div className="mt-6">
          <div className="flex flex-wrap gap-2 justify-between items-center mb-4">
            <div className="flex gap-2 flex-wrap">
              {[
                { id: "todos", label: "Todas" },
                { id: "individual", label: "Individuais" },
                { id: "duo", label: "Duo" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setSessionFilter(f.id)}
                  className="chip"
                  data-active={sessionFilter === f.id}
                  data-testid={`session-filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setShareOpen(true)}
                disabled={sessions.length === 0}
                className="btn-ghost inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="share-sessions-btn"
                title="Partilhar histórico de sessões"
              >
                <Share2 size={14} /> Partilhar
              </button>
              <button
                onClick={() => setShowSession(true)}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="add-session-btn"
              >
                <Plus size={16} /> Registar sessão
              </button>
            </div>
          </div>

          {filteredSessions.length === 0 ? (
            <div className="card p-8 text-center text-neutral-500">
              Sem sessões registadas neste filtro.
            </div>
          ) : (
            <ul className="divide-y divide-neutral-900 border-y border-neutral-900">
              {filteredSessions.map((s) => (
                <li
                  key={s.id}
                  className="py-4 grid grid-cols-12 gap-3 items-center"
                  data-testid={`session-row-${s.id}`}
                >
                  <div className="col-span-3 md:col-span-2 font-display text-yellow-400 text-lg">
                    {s.data}
                  </div>
                  <div className="col-span-5 md:col-span-6">
                    <div className="font-medium">{s.titulo || "Sessão de treino"}</div>
                    {s.notas ? (
                      <div className="text-xs text-neutral-500 mt-1">{s.notas}</div>
                    ) : null}
                  </div>
                  <div className="col-span-3 md:col-span-3 flex flex-wrap gap-2">
                    <span className="chip">
                      {s.tipo === "duo" ? (
                        <>
                          <Users size={12} /> Duo
                        </>
                      ) : (
                        <>
                          <User size={12} /> Indiv.
                        </>
                      )}
                    </span>
                    <span className="chip">{s.duracao_min}m</span>
                  </div>
                  <div className="col-span-1 text-right">
                    <button
                      onClick={() => removeSession(s.id)}
                      className="text-neutral-500 hover:text-yellow-400"
                      data-testid={`session-del-${s.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {tab === "galeria" ? (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-neutral-500">
              {photos.length === 0
                ? "Sem fotos. Documenta a evolução visual com fotos datadas."
                : `${photos.length} fotos guardadas`}
            </div>
            <button
              onClick={() => setShowPhoto(true)}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="add-photo-btn"
            >
              <Plus size={16} /> Adicionar foto
            </button>
          </div>
          {photos.length === 0 ? (
            <div className="card p-12 text-center">
              <ImageIcon size={36} className="mx-auto text-neutral-700 mb-3" />
              <div className="text-neutral-400">Sem fotos ainda.</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {photos.map((p) => (
                <div
                  key={p.id}
                  className="card p-0 relative group overflow-hidden"
                  data-testid={`photo-card-${p.id}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={p.foto}
                      alt={p.notas || "foto cliente"}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                  </div>
                  <div className="p-3 border-t border-neutral-900">
                    <div className="text-xs font-display text-yellow-400">
                      {p.data || new Date(p.created_at).toLocaleDateString("pt-PT")}
                    </div>
                    {p.notas ? (
                      <div className="text-[11px] text-neutral-500 mt-1 truncate">
                        {p.notas}
                      </div>
                    ) : null}
                  </div>
                  <button
                    onClick={() => removePhoto(p.id)}
                    className="absolute top-2 right-2 w-8 h-8 bg-black/80 border border-neutral-700 flex items-center justify-center text-neutral-300 hover:text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`photo-del-${p.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {tab === "evolucao" ? (
        <div className="mt-6 card p-6">
          {evolutionData.length < 2 ? (
            <div className="text-center text-neutral-500 py-10">
              <TrendingUp className="mx-auto mb-3 text-neutral-700" size={36} />
              Adiciona pelo menos 2 avaliações para ver a evolução.
            </div>
          ) : (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <LineChart data={evolutionData}>
                  <CartesianGrid stroke="#222" strokeDasharray="2 4" />
                  <XAxis dataKey="data" stroke="#666" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#666" tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      background: "#0a0a0a",
                      border: "1px solid #262626",
                      color: "#fafafa",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="peso"
                    name="Peso (kg)"
                    stroke="#FACC15"
                    strokeWidth={2}
                    dot={{ fill: "#FACC15", r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="massa_adiposa"
                    name="Massa Adiposa (%)"
                    stroke="#fafafa"
                    strokeWidth={2}
                    dot={{ fill: "#fafafa", r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      ) : null}

      {showAssess ? (
        <AssessmentForm
          clientId={assessTargetId || clientId}
          onClose={() => { setShowAssess(false); setAssessTargetId(null); }}
          onSaved={async (saved) => {
            // Determinar qual é o cliente-alvo
            const isPartner = assessTargetId && assessTargetId !== clientId;
            const targetId = assessTargetId || clientId;
            let targetClient = client;
            let prevList = assessments;
            if (isPartner) {
              try {
                const r = await axios.get(`${API}/clients/${targetId}`);
                targetClient = r.data;
              } catch {}
              prevList = partnerAssessments;
            }
            // A avaliação anterior é a última antes da nova (por data)
            const savedDate = saved?.data;
            const prev = prevList
              .filter((a) => a.id !== saved?.id && (!savedDate || a.data <= savedDate))
              .sort((a, b) => (a.data > b.data ? 1 : -1))
              .slice(-1)[0];
            await reload();
            if (saved) {
              setShareData({ client: targetClient, assessment: saved, prev });
            }
          }}
        />
      ) : null}
      <ShareAssessmentModal
        open={!!shareData}
        onClose={() => setShareData(null)}
        targetClient={shareData?.client}
        assessment={shareData?.assessment}
        prevAssessment={shareData?.prev}
      />
      {showSession ? (
        <SessionForm
          clientId={clientId}
          defaultTipo={client.tipo}
          onClose={() => setShowSession(false)}
          onSaved={() => {
            reload();
            onChanged && onChanged();
          }}
        />
      ) : null}
      {showEdit ? (
        <ClientFormModal
          open
          initial={client}
          allClients={allClients}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            reload();
            onChanged && onChanged();
          }}
        />
      ) : null}
      {showPhoto ? (
        <PhotoGalleryForm
          clientId={clientId}
          onClose={() => setShowPhoto(false)}
          onSaved={reload}
        />
      ) : null}
      {viewAssess ? (
        <AssessmentDetail a={viewAssess} onClose={() => setViewAssess(null)} />
      ) : null}
      {shareOpen ? (
        <ShareSessionsModal
          clientName={client?.nome || ""}
          shareText={shareText}
          whatsappUrl={whatsappUrl}
          onCopy={handleCopy}
          onNativeShare={handleNativeShare}
          copied={shareCopied}
          onClose={() => setShareOpen(false)}
        />
      ) : null}
    </div>
  );
}

function ShareSessionsModal({ clientName, shareText, whatsappUrl, onCopy, onNativeShare, copied, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="share-modal-backdrop"
    >
      <div
        className="card w-full max-w-xl p-6 md:p-7 relative max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal"
      >
        <button
          className="absolute top-4 right-4 text-neutral-500 hover:text-yellow-400 p-1"
          onClick={onClose}
          aria-label="Fechar"
          data-testid="share-modal-close"
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-yellow-400/15 border border-yellow-400/40 flex items-center justify-center">
            <Share2 size={18} className="text-yellow-400" />
          </div>
          <div>
            <h3 className="font-display text-xl uppercase tracking-tight">
              Partilhar histórico
            </h3>
            <p className="text-xs text-neutral-400">
              Sessões realizadas por {clientName} até hoje
            </p>
          </div>
        </div>

        <textarea
          readOnly
          value={shareText}
          rows={10}
          className="input w-full text-sm flex-1 mb-4 resize-none whitespace-pre-wrap"
          data-testid="share-text"
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center justify-center gap-2"
            data-testid="share-whatsapp"
          >
            <MessageCircle size={14} /> WhatsApp
          </a>
          <button
            type="button"
            onClick={onNativeShare}
            className="btn-ghost inline-flex items-center justify-center gap-2"
            data-testid="share-native"
          >
            <Share2 size={14} /> Partilhar...
          </button>
          <button
            type="button"
            onClick={onCopy}
            className="btn-ghost inline-flex items-center justify-center gap-2"
            data-testid="share-copy"
          >
            {copied ? <Check size={14} className="text-yellow-400" /> : <Copy size={14} />}
            {copied ? "Copiado!" : "Copiar"}
          </button>
        </div>

        <p className="text-[11px] text-neutral-500 mt-3 text-center">
          O texto inclui todas as sessões com data até hoje.
        </p>
      </div>
    </div>
  );
}

export default function ClientesView() {
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("todos");
  const [showForm, setShowForm] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const load = async () => {
    const res = await axios.get(`${API}/clients`);
    setClients(res.data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      filter === "todos"
        ? clients
        : clients.filter((c) => c.tipo === filter),
    [clients, filter]
  );

  const totals = useMemo(() => {
    const ind = clients.filter((c) => c.tipo === "individual").length;
    const duo = clients.filter((c) => c.tipo === "duo").length;
    const month = clients.reduce((s, c) => s + (c.sessoes_este_mes || 0), 0);
    const total = clients.reduce((s, c) => s + (c.sessoes_total || 0), 0);
    return { ind, duo, month, total };
  }, [clients]);

  if (activeId) {
    return (
      <ClientDetail
        clientId={activeId}
        onBack={() => setActiveId(null)}
        onChanged={load}
        allClients={clients}
      />
    );
  }

  return (
    <section className="px-6 md:px-12 lg:px-24 py-12 md:py-16" data-testid="clientes-view">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="overline mb-3">Gestão · Clientes</div>
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter">
            Os teus clientes
          </h2>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="add-client-btn"
        >
          <Plus size={16} /> Novo cliente
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="border border-neutral-800 p-4">
          <div className="font-display text-3xl text-yellow-400">{totals.ind}</div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            Individuais
          </div>
        </div>
        <div className="border border-neutral-800 p-4">
          <div className="font-display text-3xl text-yellow-400">{totals.duo}</div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            Duo
          </div>
        </div>
        <div className="border border-neutral-800 p-4">
          <div className="font-display text-3xl">{totals.month}</div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            Treinos este mês
          </div>
        </div>
        <div className="border border-neutral-800 p-4">
          <div className="font-display text-3xl">{totals.total}</div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            Treinos totais
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {[
          { id: "todos", label: "Todos", icon: Users },
          { id: "individual", label: "Individuais", icon: User },
          { id: "duo", label: "Duo", icon: Users },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className="chip"
            data-active={filter === f.id}
            data-testid={`filter-${f.id}`}
          >
            <f.icon size={14} /> {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <Users size={36} className="mx-auto text-neutral-700 mb-3" />
          <div className="text-neutral-400">
            Ainda não tens clientes neste filtro.
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary mt-5 inline-flex items-center gap-2"
          >
            <Plus size={14} /> Adicionar primeiro cliente
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <ClientCard key={c.id} c={c} onOpen={setActiveId} />
          ))}
        </div>
      )}

      {showForm ? (
        <ClientFormModal
          open
          onClose={() => setShowForm(false)}
          onSaved={load}
          allClients={clients}
        />
      ) : null}
    </section>
  );
}
